// Fetching applicant documents (photo / signature) from the protected
// /uploads route.
//
// This previously exported getFileUrl(), which appended the caller's bearer
// token to the URL as a query parameter so it could be used directly in an
// <img src>. That put a full-privilege credential — preferring the 8-hour
// admin token — somewhere URLs routinely get recorded: platform and proxy
// access logs, browser history, and the Referer header on outbound links.
//
// Documents are now fetched with an Authorization header and rendered from a
// blob URL by <ProtectedImage>. Nothing sensitive appears in the URL.
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

// The /uploads route lives at the server root, not under /api.
const filesOrigin = () => API_BASE_URL.replace(/\/api\/?$/, '');

/**
 * Fetches a protected applicant document and resolves to a Blob.
 *
 * Sends the admin token when present (an admin may view any applicant's
 * documents) and otherwise the logged-in applicant's token, which the server
 * checks against that applicant's own application.
 */
export const fetchProtectedFile = async (pathOrName) => {
  if (!pathOrName) throw new Error('No file specified');

  // Legacy absolute URLs are returned as-is by the caller's own logic; here we
  // only handle files served by our own /uploads route.
  const filename = String(pathOrName).split('/').pop();

  const adminToken = localStorage.getItem('adminToken');
  const userToken = localStorage.getItem('token');
  const token = adminToken || userToken;

  if (!token) throw new Error('Not authenticated');

  const headers = adminToken
    ? { 'X-Admin-Token': adminToken }
    : { Authorization: `Bearer ${userToken}` };

  const response = await fetch(
    `${filesOrigin()}/uploads/${encodeURIComponent(filename)}`,
    { headers }
  );

  if (!response.ok) {
    throw new Error(`Could not load document (HTTP ${response.status})`);
  }

  return response.blob();
};
